import React from "react";
import { Card, CardContent, Typography, Box, Chip, useTheme, alpha } from "@mui/material";
import { TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon } from "@mui/icons-material";
import { TournamentPointsTrendEntry } from "../hooks/useTournamentStats";

interface TournamentPointsTrendProps {
  pointsTrend: TournamentPointsTrendEntry[];
}

interface ChartPoint {
  x: number;
  y: number;
  entry: TournamentPointsTrendEntry;
}

function buildSmoothPath(points: ChartPoint[]): string {
  if (points.length === 0) return "";
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i];
    const p1 = points[i + 1];
    const midX = (p0.x + p1.x) / 2;
    d += ` C ${midX} ${p0.y}, ${midX} ${p1.y}, ${p1.x} ${p1.y}`;
  }
  return d;
}

const TournamentPointsTrend: React.FC<TournamentPointsTrendProps> = ({
  pointsTrend,
}) => {
  const theme = useTheme();

  if (pointsTrend.length < 2) {
    return (
      <Card sx={{ borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
            Points Trend
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Not enough completed tournaments yet to show a trend.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const maxPoints = Math.max(...pointsTrend.map((p) => p.points), 1);
  const chartWidth = 1000;
  const chartHeight = 220;
  const padding = 24;
  const baselineY = chartHeight - padding;
  const pointSpacing = (chartWidth - 2 * padding) / (pointsTrend.length - 1 || 1);

  const points: ChartPoint[] = pointsTrend.map((entry, index) => {
    const x = padding + index * pointSpacing;
    const y = baselineY - (entry.points / maxPoints) * (baselineY - padding);
    return { x, y, entry };
  });

  const linePath = buildSmoothPath(points);
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${baselineY} L ${points[0].x} ${baselineY} Z`;

  const latest = pointsTrend[pointsTrend.length - 1];
  const previous = pointsTrend[pointsTrend.length - 2];
  const delta = latest.points - previous.points;
  const isUp = delta >= 0;
  const trendColor = isUp ? theme.palette.success.main : theme.palette.error.main;

  return (
    <Card sx={{ borderRadius: 3 }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            Points Trend
          </Typography>
          <Chip
            icon={isUp ? <TrendingUpIcon /> : <TrendingDownIcon />}
            label={`${isUp ? "+" : ""}${delta} vs last`}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: alpha(trendColor, 0.15),
              color: trendColor,
              "& .MuiChip-icon": { color: trendColor },
            }}
          />
        </Box>
        <Box sx={{ position: "relative", height: 260, pt: 2 }}>
          <Box
            sx={{
              height: "calc(100% - 40px)",
              position: "relative",
              px: 1,
            }}
          >
            <svg
              width="100%"
              height="100%"
              viewBox={`0 0 ${chartWidth} ${chartHeight}`}
              preserveAspectRatio="none"
              style={{ overflow: "visible" }}
            >
              <defs>
                <linearGradient id="tournamentPointsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={theme.palette.primary.main} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={theme.palette.primary.main} stopOpacity={0} />
                </linearGradient>
              </defs>

              {/* horizontal gridlines */}
              {[0.25, 0.5, 0.75, 1].map((frac) => (
                <line
                  key={frac}
                  x1={padding}
                  x2={chartWidth - padding}
                  y1={baselineY - frac * (baselineY - padding)}
                  y2={baselineY - frac * (baselineY - padding)}
                  stroke={alpha(theme.palette.text.primary, 0.06)}
                  strokeWidth="1"
                  vectorEffect="non-scaling-stroke"
                />
              ))}

              <path d={areaPath} fill="url(#tournamentPointsFill)" stroke="none" />

              <path
                d={linePath}
                fill="none"
                stroke={theme.palette.primary.main}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />

              {points.map((point, index) => {
                const isLast = index === points.length - 1;
                return (
                  <g key={point.entry.tournamentId}>
                    {isLast && (
                      <circle
                        cx={point.x}
                        cy={point.y}
                        r="9"
                        fill={alpha(theme.palette.primary.main, 0.2)}
                      />
                    )}
                    <circle
                      cx={point.x}
                      cy={point.y}
                      r={isLast ? "5.5" : "4"}
                      fill={theme.palette.primary.main}
                      stroke="white"
                      strokeWidth="2"
                      vectorEffect="non-scaling-stroke"
                    />
                    <text
                      x={point.x}
                      y={point.y - 16}
                      textAnchor="middle"
                      fontSize="13"
                      fontWeight="bold"
                      fill={theme.palette.text.primary}
                      transform={`translate(${point.x}, ${point.y - 16}) scale(1.4, 1) translate(${-point.x}, ${-(point.y - 16)})`}
                    >
                      {point.entry.points}
                    </text>
                  </g>
                );
              })}
            </svg>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 0.5,
              px: 1,
            }}
          >
            {pointsTrend.map((entry) => (
              <Box key={entry.tournamentId} sx={{ textAlign: "center", minWidth: 0, maxWidth: 100 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.65rem",
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {entry.tournamentName}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.6rem", display: "block" }}
                >
                  {new Date(entry.startDate).toLocaleDateString("en-US", {
                    month: "short",
                    day: "numeric",
                  })}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default TournamentPointsTrend;
